/**
 * Finds tour targets by what users see (titles, field labels) and by ARIA attributes, so guides
 * follow the pages without test ids in every plugin. No MUI class selectors: in production MUI v4
 * classes are minified (`jss4-123`). A target that is not found makes Joyride skip the step.
 */

type Target = () => HTMLElement | null;

/**
 * The most specific element matching `selector` whose text starts with `text`: a wrapper whose
 * text happens to start the same way is skipped for the element inside it.
 */
export function findByText(selector: string, text: string): HTMLElement | null {
  const matches = Array.from(
    document.querySelectorAll<HTMLElement>(selector),
  ).filter(el => el.textContent?.trim().startsWith(text));
  return (
    matches.find(
      el => !matches.some(other => other !== el && el.contains(other)),
    ) ?? null
  );
}

export const byText =
  (selector: string, text: string): Target =>
  () =>
    findByText(selector, text);

/** A form field (text field, select, radio group) by its label or legend. */
export const field =
  (label: string): Target =>
  () => {
    const el = findByText('label, legend', label);
    if (!el) return null;
    return el.tagName === 'LEGEND'
      ? el.closest<HTMLElement>('fieldset')
      : el.parentElement;
  };

/** A card (Backstage InfoCard) by its title: the tooltip points at the title. */
export const card = (title: string): Target =>
  byText('h1, h2, h3, h4, h5, h6, span', title);

/** Title of an open side drawer (FormDrawer renders it as an h6 in the MUI modal). */
const drawerTitle = (title: string) =>
  findByText('[role="presentation"] h6', title);

export const drawerOpen = (title: string) => Boolean(drawerTitle(title));

/** The open drawer whose title starts with `title`. */
export const drawer =
  (title: string): Target =>
  () =>
    drawerTitle(title)?.parentElement ?? null;

/** The ticket's runs drawer (TicketRunDrawer labels its panel « Exécutions de MP-… »). */
export const runsDrawerOpen = () =>
  Boolean(document.querySelector('[aria-label^="Exécutions de "]'));

/** No side drawer is open: page guides wait until drawers are closed. */
export const noDrawer = () =>
  !document.querySelector('[role="presentation"] h6') && !runsDrawerOpen();
